import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/SectionLabel";
import { Lightbox } from "@/components/Gallery";
import { GALLERY_CATEGORIES, GALLERY_IMAGES } from "@/data/eventData";

const ALL = "All";

const BackLink = ({ testId }) => (
    <Link
        to="/"
        data-testid={testId}
        className="group inline-flex items-center gap-2.5 font-sans text-[11px] font-semibold uppercase tracking-[0.3em] text-ash transition-colors duration-300 hover:text-cream"
    >
        <ArrowLeft className="h-4 w-4 text-scarlet transition-transform duration-300 group-hover:-translate-x-1" />
        Back to I-ACE
    </Link>
);

export default function GalleryPage() {
    const [filter, setFilter] = useState(ALL);
    const [active, setActive] = useState(null);

    const categories = useMemo(
        () => [
            ALL,
            ...GALLERY_CATEGORIES.filter((cat) =>
                GALLERY_IMAGES.some((photo) => photo.category === cat)
            ),
        ],
        []
    );

    const photos = useMemo(
        () =>
            filter === ALL
                ? GALLERY_IMAGES
                : GALLERY_IMAGES.filter((photo) => photo.category === filter),
        [filter]
    );

    return (
        <>
            <section className="relative overflow-hidden pb-12 pt-32 md:pb-16 md:pt-44">
                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-[radial-gradient(ellipse_80%_55%_at_50%_-10%,rgba(201,31,38,0.16),transparent_62%)]"
                />
                <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
                    <Reveal y={16}>
                        <BackLink testId="gallery-back-link" />
                    </Reveal>
                    <Reveal y={20} delay={0.05}>
                        <div className="mt-10">
                            <SectionLabel index="07" text="The Gallery" />
                        </div>
                    </Reveal>
                    <Reveal y={20} delay={0.1}>
                        <h1
                            data-testid="gallery-page-heading"
                            className="mt-8 font-serif text-5xl font-light leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-7xl"
                        >
                            Moments.
                            <br />
                            That <em className="italic text-scarlet">Stay.</em>
                        </h1>
                    </Reveal>
                    <Reveal y={20} delay={0.18}>
                        <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-ash md:text-lg">
                            A visual archive of debates, people, victories and moments
                            from I-ACE.
                        </p>
                    </Reveal>

                    <Reveal y={16} delay={0.22}>
                        <div
                            role="tablist"
                            aria-label="Filter photographs"
                            className="no-scrollbar mt-12 flex gap-7 overflow-x-auto border-b border-cream/8"
                        >
                            {categories.map((cat) => {
                                const isActive = filter === cat;
                                return (
                                    <button
                                        key={cat}
                                        type="button"
                                        role="tab"
                                        aria-selected={isActive}
                                        data-testid={`gallery-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                                        onClick={() => {
                                            setFilter(cat);
                                            setActive(null);
                                        }}
                                        className={`relative shrink-0 whitespace-nowrap pb-3.5 pt-1 font-sans text-[10px] font-semibold uppercase tracking-[0.25em] transition-colors duration-300 ${
                                            isActive
                                                ? "text-cream"
                                                : "text-ash/50 hover:text-cream"
                                        }`}
                                    >
                                        {cat}
                                        {isActive && (
                                            <motion.span
                                                layoutId="gallery-filter-underline"
                                                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                                className="absolute inset-x-0 bottom-0 h-[2px] bg-scarlet"
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </Reveal>
                </div>
            </section>

            <section data-testid="archive-grid" className="pb-16">
                <div className="mx-auto max-w-[1400px] columns-2 gap-4 px-5 md:columns-3 md:px-10 lg:columns-4">
                    {photos.map((photo, i) => (
                        <motion.button
                            key={photo.src}
                            type="button"
                            onClick={() => setActive(i)}
                            data-testid={`archive-photo-${i}`}
                            aria-label={`Open photo: ${photo.caption}`}
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.5,
                                delay: Math.min(i * 0.04, 0.4),
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="group relative mb-4 block w-full break-inside-avoid overflow-hidden border border-cream/10 text-left"
                        >
                            <img
                                src={photo.src}
                                alt={photo.caption}
                                loading="lazy"
                                className={`w-full object-cover transition-transform duration-700 group-hover:scale-[1.04] ${
                                    photo.focus || ""
                                }`}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                            <div className="absolute inset-x-0 bottom-0 translate-y-2 p-4 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                                <p className="font-sans text-[9px] font-semibold uppercase tracking-[0.3em] text-scarlet">
                                    {photo.category} · {photo.edition}
                                </p>
                                <p className="mt-1 text-sm font-light text-cream">
                                    {photo.caption}
                                </p>
                            </div>
                        </motion.button>
                    ))}
                </div>
            </section>

            <div className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10 md:pb-32">
                <BackLink testId="gallery-back-link-bottom" />
            </div>

            <AnimatePresence>
                {active !== null && photos[active] && (
                    <Lightbox
                        photo={photos[active]}
                        index={active}
                        total={photos.length}
                        onClose={() => setActive(null)}
                        onPrev={() =>
                            setActive((a) => (a - 1 + photos.length) % photos.length)
                        }
                        onNext={() => setActive((a) => (a + 1) % photos.length)}
                    />
                )}
            </AnimatePresence>
        </>
    );
}
